import { useEffect, useState } from 'react';
import {
  Box,
  Button,
  Checkbox,
  Heading,
  Label,
  LabeledControl,
  Modal,
  Paragraph,
  Text,
} from '@zillow/constellation-10';
import { MAX_COMPETITORS } from './RentComps.utils';

type EditCompetitorsModalProps = {
  open: boolean;
  onClose: () => void;
  availableCompNames: Array<string>;
  selectedCompNames: Array<string>;
  onConfirm: (selected: Array<string>) => void;
};

export const EditCompetitorsModal = ({
  open,
  onClose,
  availableCompNames,
  selectedCompNames,
  onConfirm,
}: EditCompetitorsModalProps) => {
  const [editableNames, setEditableNames] = useState<Set<string>>(new Set(selectedCompNames));

  useEffect(() => {
    if (open) {
      setEditableNames(new Set(selectedCompNames));
    }
  }, [open, selectedCompNames]);

  const handleToggle = (name: string) => {
    setEditableNames((prev) => {
      const next = new Set(prev);
      if (next.has(name)) {
        next.delete(name);
      } else if (next.size < MAX_COMPETITORS) {
        next.add(name);
      }
      return next;
    });
  };

  const handleConfirm = () => {
    onConfirm(availableCompNames.filter((name) => editableNames.has(name)));
    onClose();
  };

  const topFive = availableCompNames.slice(0, 5);
  const rest = availableCompNames.slice(5);

  return (
    <Modal
      size="lg"
      open={open}
      onOpenChange={(isOpen) => {
        if (!isOpen) onClose();
      }}
      header={
        <Box css={{ display: 'flex', alignItems: 'center', width: '100%' }}>
          <Heading level={5}>Customize your comps</Heading>
        </Box>
      }
      body={
        <Box css={{ padding: '600', display: 'flex', flexDirection: 'column', gap: '400' }}>
          <Paragraph>Select up to {MAX_COMPETITORS} comps to compare against.</Paragraph>
          {topFive.length > 0 && (
            <Box css={{ display: 'flex', flexDirection: 'column', gap: '200' }}>
              <Text textStyle="body-sm-bold">Top 5</Text>
              {topFive.map((name) => (
                <LabeledControl
                  key={name}
                  control={
                    <Checkbox
                      checked={editableNames.has(name)}
                      onChange={() => handleToggle(name)}
                      disabled={!editableNames.has(name) && editableNames.size >= MAX_COMPETITORS}
                    />
                  }
                  label={<Label>{name}</Label>}
                />
              ))}
            </Box>
          )}
          {rest.length > 0 && (
            <Box css={{ display: 'flex', flexDirection: 'column', gap: '200' }}>
              <Text textStyle="body-sm-bold">Top 6-{5 + rest.length}</Text>
              {rest.map((name) => (
                <LabeledControl
                  key={name}
                  control={
                    <Checkbox
                      checked={editableNames.has(name)}
                      onChange={() => handleToggle(name)}
                      disabled={!editableNames.has(name) && editableNames.size >= MAX_COMPETITORS}
                    />
                  }
                  label={<Label>{name}</Label>}
                />
              ))}
            </Box>
          )}
        </Box>
      }
      footer={
        <Box css={{ display: 'flex', justifyContent: 'flex-end', gap: '400' }}>
          <Button tone="neutral" emphasis="outlined" onClick={onClose}>
            Cancel
          </Button>
          <Button tone="brand" emphasis="filled" onClick={handleConfirm}>
            Confirm
          </Button>
        </Box>
      }
    />
  );
};
